import useAuth from '@app/auth/use-auth';
import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { makeUpdateItemsAction } from '@app/components/item-edit/update-items-modal-body';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import { commonFilterParsers } from '@app/hooks/use-data-table-query-state';
import { buildItemScopeSearch } from '@app/purchased-goods/components/item-detail/use-item-scope';
import {
  PackagingActivitiesList,
  PaginatedPackagingActivitiesListList,
} from '@shared/api/types';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { purchasedGoodsRoutes } from '../../purchased-goods-routes';
import {
  PackagingFilterColumn,
  packagingFilterKeys,
  PackagingFilterOption,
  packagingFilters,
} from '../filters';
import { usePurchasedPackagingTableColumns } from './purchased-packaging-table-columns';
import PurchasedPackagingTableToolbar from './purchased-packaging-table-toolbar';

interface PurchasedPackagingTableProps {
  data?: PaginatedPackagingActivitiesListList;
  pageSize?: number;
  status?: string;
  isLoading?: boolean;
  isPreviousData?: boolean;
  isFetching?: boolean;
}

export default function PurchasedPackagingTable({
  pageSize,
  status,
  data,
  isLoading,
  isFetching,
}: PurchasedPackagingTableProps) {
  const [site] = useQueryState('site', commonFilterParsers.site);
  const [period] = useQueryState('period', commonFilterParsers.period);
  const navigate = useNavigate();
  const { user } = useAuth();

  // A single selected period maps onto the detail page's range as both
  // boundaries; any other selection has no equivalent, so the detail page
  // falls back to its default window.
  const drillPeriod = period.length === 1 ? period[0] : null;
  const drillScopeSearch = buildItemScopeSearch({
    periodStart: drillPeriod,
    periodEnd: drillPeriod,
    sites: site,
  });

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (pageSize || 10)) : 0;

  const filterOptions = useServerFilterOptions(
    packagingFilterKeys,
    data?.filters || {}
  );

  const useFilterOptions = useMemo(() => {
    const opts: FilterOptions<PackagingFilterOption[]> = {} as FilterOptions<
      PackagingFilterOption[]
    >;
    packagingFilterKeys.forEach((key) => {
      opts[key] = filterOptions[`${key}Filter`];
    });
    return opts;
  }, [filterOptions]);

  const columns = usePurchasedPackagingTableColumns({
    filters: useFilterOptions,
    timePeriods: toPickerPeriods(data?.filters?.timePeriods),
  });

  const { table, context } = useServerDataTable({
    data: data?.results || [],
    columns: [
      // Selection checkboxes are staff-only (bulk actions are gated to staff).
      ...(user?.isStaff ? [makeSelectColumn<PackagingActivitiesList>()] : []),
      ...columns,
    ],
    pageCount,
    shallow: false,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        remoteId: false,
        [PackagingFilterColumn.Period]: false,
        [PackagingFilterColumn.Site]: false,
        [PackagingFilterColumn.Supplier]: false,
        [PackagingFilterColumn.ActivitiesImport]: false,
        [PackagingFilterColumn.Tag]: false,
      },
      columnOrder: ['remoteId', 'supplier', 'packaging'],
    },
  });

  // "Update items" hides its own "Packaging" option since these items are
  // already packaging. Rows are keyed by activity uuid; change-type acts on
  // the PG.
  const bulkActions = useMemo(
    () => [
      makeUpdateItemsAction({
        currentType: 'packaging',
        resolveUuids: (table) =>
          table
            .getFilteredSelectedRowModel()
            .rows.map(
              (row) =>
                (row.original as PackagingActivitiesList).purchasedGoodInfo.uuid
            ),
      }),
    ],
    []
  );

  return (
    <>
      <DataTable
        table={table}
        context={context}
        tableLoading={!data && status === 'loading'}
        isLoading={isLoading || isFetching}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) =>
          navigate(
            generatePath(purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM, {
              item_uuid: row?.original.uuid,
            }) + drillScopeSearch
          )
        }
      >
        <DataTableToolbar
          table={table}
          filterOrder={[
            'packagingName',
            ...Object.values(packagingFilters).map((f) => f.column),
          ]}
        >
          <PurchasedPackagingTableToolbar />
        </DataTableToolbar>
      </DataTable>
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
}
